// Add-to-playlist control — a small popover that lists the viewer's personal playlists and
// drops the given track into one (or creates a new playlist on the spot). Reused from search
// rows and anywhere a MusicItem can be saved. Personal, owner-scoped; external refs only.
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ListPlus, Plus, Loader2, Check } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import type { MusicItem } from './lib/types';
import * as api from '@/services/performanceService';

export const AddToPlaylistButton: React.FC<{ item: MusicItem; className?: string }> = ({ item, className }) => {
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState<api.MusicPlaylist[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [addedTo, setAddedTo] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setLists(null); setCreating(false); setNewName(''); setAddedTo(null);
    api.listMyPlaylists().then(setLists).catch(() => setLists([]));
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  if (!item.canonicalUrl) return null;

  const add = async (playlistId: string, name: string) => {
    setBusy(true);
    try {
      await api.addMyPlaylistItem(playlistId, { url: item.canonicalUrl!, title: item.title, artist: item.artist });
      setAddedTo(playlistId);
      toast.success(`Added to ${name}`);
      window.setTimeout(() => setOpen(false), 600);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Could not add to playlist')); }
    finally { setBusy(false); }
  };
  const createAndAdd = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const p = await api.createMyPlaylist({ name: newName.trim() });
      await api.addMyPlaylistItem(p.id, { url: item.canonicalUrl!, title: item.title, artist: item.artist });
      toast.success(`Added to ${p.name}`);
      setOpen(false);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Could not create playlist')); }
    finally { setBusy(false); }
  };

  return (
    <div ref={ref} className="relative">
      <button type="button" aria-label="Add to playlist" title="Add to playlist" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={className ?? 'rounded-md p-1.5 text-muted-foreground hover:bg-card hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400'}>
        <ListPlus className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-60 rounded-xl border border-border bg-card p-1.5 shadow-xl">
          <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Add to playlist</div>

          {lists === null ? (
            <div className="flex items-center justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="max-h-56 overflow-y-auto">
              {lists.map((p) => (
                <button key={p.id} type="button" disabled={busy} onClick={() => add(p.id, p.name)}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:opacity-60">
                  <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  {addedTo === p.id ? <Check className="h-4 w-4 text-green-500" /> : <span className="text-[11px] text-muted-foreground">{p.item_count ?? 0}</span>}
                </button>
              ))}
              {lists.length === 0 && !creating && <p className="px-2 py-2 text-xs text-muted-foreground">No playlists yet — create one below.</p>}
            </div>
          )}

          <div className="mt-1 border-t border-border pt-1">
            {creating ? (
              <div className="flex items-center gap-1.5 p-1">
                <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void createAndAdd(); }}
                  placeholder="New playlist name"
                  className="min-w-0 flex-1 rounded-md border border-border bg-card px-2 py-1 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400" />
                <button type="button" disabled={busy || !newName.trim()} onClick={() => void createAndAdd()}
                  className="rounded-md bg-primary-500 px-2 py-1 text-xs font-semibold text-white hover:bg-primary-600 disabled:opacity-50">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add'}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setCreating(true)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm font-medium text-primary-600 hover:bg-muted">
                <Plus className="h-4 w-4" /> New playlist
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
